import { Creatable, Deletable } from '@/types/model.types';
import { PlatformCreateNavigationItem } from '@data/_generated-api/platform-api/models/platform-create-navigation-item';
import { NavigationItemType } from '@shared/enum/navigation-item-type';
import { PlatformNavigationItemType } from '@data/_generated-api/platform-api/models/platform-navigation-item-type';
import { EgiPage } from '@ervo/page-framework';
import { EgiAppEntity } from '@/app/platform/platform-data/models/egi-app.entity';
import { genericAppIndexId } from '@/app/egi.pages/app-root.pages/page-ids';
import { NavigationItemData } from '@/app/platform/platform-shared/models/navigation-item.data';

/**
 * @inheritDoc
 */
export class NavigationItemEntity
  extends NavigationItemData
  implements Deletable<string>, Creatable<PlatformCreateNavigationItem> {
  get creationModel(): PlatformCreateNavigationItem {
    return {
      appId: this.appId,
      name: this.name,
      pageComponentId: this.pageComponentId,
      parentId: this.parentId,
      type: (this.type as unknown) as PlatformNavigationItemType
    };
  }

  get deleteKey(): string {
    return this.id;
  }

  get isNameEditable(): boolean {
    return !this.isRoot && !this.isClickTroughPage;
  }

  get canBeChangedPosition(): boolean {
    return !this.isRoot && this.type !== NavigationItemType.ClickThrough;
  }

  get isConfigurable(): boolean {
    return !!this.egiPage?.config.configurationOptions;
  }

  isSiblingOf(i: NavigationItemEntity): boolean {
    return this.parentId === i.parentId;
  }

  addChild(item: this) {
    this.children.push(item);
  }

  makeFirstChildOf(parent: NavigationItemEntity): void {
    this.parentId = parent.id;
    this.order = 0;
  }

  makeSiblingOf(sibling: NavigationItemEntity): void {
    this.parentId = sibling.parentId;
  }

  isSiblingAbove(target: NavigationItemEntity): boolean {
    return this.isSiblingOf(target) && this.order < target.order;
  }

  isFirstSiblingAbove(target: NavigationItemEntity): boolean {
    return this.isSiblingAbove(target) && this.order + 1 === target.order;
  }

  moveUp(count = 1): void {
    if (this.order > 0) {
      this.order -= count;
    }
  }

  moveDown(count = 1): void {
    this.order += count;
  }

  takePlaceOf(target: NavigationItemEntity): void {
    this.order = target.order;
  }

  isRelatedTo(target: NavigationItemEntity): boolean {
    return target.appId === this.appId;
  }

  descendantIsIncompatibleWith(app: EgiAppEntity): boolean {
    return this.descendants.some(d => !d.isCompatibleWith(app));
  }

  isCompatibleWith(app: EgiAppEntity): boolean {
    if (this.type === NavigationItemType.Menu) {
      return true;
    }

    return app.isCompatibleWith(this.egiPage);
  }

  public static createDefault(type?: NavigationItemType, id = ''): NavigationItemEntity {
    return new NavigationItemEntity(
      id,
      {},
      '',
      '',
      0,
      [],
      genericAppIndexId,
      type ?? NavigationItemType.Unknown,
      undefined,
      '',
      undefined
    );
  }

  public static createFromPage(page: EgiPage, parent: NavigationItemEntity): NavigationItemEntity {
    const item = NavigationItemEntity.createDefault(NavigationItemType.Page);
    item.name = page.config.name;
    item.pageComponentId = page.config.id;
    item.parentId = parent.id;
    item.appId = parent.appId;
    item.type = NavigationItemType.Page;

    return item;
  }

  public static createMenu(parent: NavigationItemEntity): NavigationItemEntity {
    const item = NavigationItemEntity.createDefault(NavigationItemType.Menu);
    item.appId = parent.appId;
    item.parentId = parent.id;
    return item;
  }

  public static createClickThroughPage(
    parent: NavigationItemEntity,
    egiPage: EgiPage
  ): NavigationItemEntity {
    const item = this.createDefault(NavigationItemType.ClickThrough);
    item.parentId = parent.id;
    item.appId = parent.appId;
    item.pageComponentId = egiPage?.config.id;
    item.name = egiPage.config.name;
    return item;
  }
}
